import { useState, useEffect, useRef, useContext } from "react";
import { NavigateFunction, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../../contexts/NotificationContext";
import NotificationBell from "src/components/reusable/NotificationBell";
import LanguageSwitcher from "src/components/LanguageSwitcher";
import Sidebar from "./Sidebar";
import { useMenu } from "src/contexts/MenuContext";
import AnnouncementPopupWrapper from "src/layouts/HomePage/SidebarManagement/AnnouncementPopupWrapper";
import SmartSearch from "src/components/reusable/SmartSearch";
import Chatbot from "../../AskDatatram/ChatLayout";
import { Layout, theme, message } from "antd";
import jwtDecode from "jwt-decode";
import DropDownProfile from "src/components/reusable/DropDownProfile";
import ErrorFallback from "src/components/reusable/ErrorFallback/ErrorFallback";
import ApiErrorContext from "src/contexts/ApiErrorContext";
import {
  DashboardApiError,
  fetchAllDashboardsAPI,
} from "src/services/AccountDashboardAPIs";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  DASHBOARD_APP_BASE,
  DASHBOARD_ROUTE_CONFIG,
  getSupportedDashboardByName,
  resolveMatchedDashboardRoute,
} from "./DashboardRoutes";
import {
  ChartArea,
  ChartNetwork,
  CircleHelp,
  SquareChartGantt,
} from "lucide-react";

interface DecodedToken {
  user_id: string;
  tenant_id: string;
  role_id: string;
  api_key: string;
}
const { Header, Content } = Layout;
const SALES_DASHBOARD_ROUTE = DASHBOARD_ROUTE_CONFIG[280].route;
const WEB_DASHBOARD_ROUTE = DASHBOARD_ROUTE_CONFIG[293].route;
const ORGANIC_DASHBOARD_ROUTE = DASHBOARD_ROUTE_CONFIG[301].route;
const CLTV_DASHBOARD_ROUTE = DASHBOARD_ROUTE_CONFIG[296].route;
const CUSTOMER_ATTRIBUTION_ROUTE = DASHBOARD_ROUTE_CONFIG[303].route;
const SALES_PRODUCT_DASHBOARD_ROUTE = DASHBOARD_ROUTE_CONFIG[304].route;
const OMNICHANNEL_DASHBOARD_ROUTE = DASHBOARD_ROUTE_CONFIG[3].route;
const PAID_MEDIA_DASHBOARD_ROUTE = DASHBOARD_ROUTE_CONFIG[302].route;

export const handleSubMenuClick = (
  dashboardName: string,
  navigate: NavigateFunction,
  setSelectedMenuItem?: React.Dispatch<React.SetStateAction<string>>
) => {
  if (setSelectedMenuItem) {
    setSelectedMenuItem(dashboardName);
  }

  const supportedDashboard = getSupportedDashboardByName(dashboardName);
  if (!supportedDashboard?.route) {
    return;
  }

  navigate(supportedDashboard.route, {
    state: { dashboardName },
  });
};

const App = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const apiErrorContext = useContext(ApiErrorContext);
  const hasGlobalApiError = apiErrorContext?.hasApiError ?? false;
  const isDashboardRoute =
    location.pathname === DASHBOARD_APP_BASE ||
    location.pathname === `${DASHBOARD_APP_BASE}/` ||
    Boolean(resolveMatchedDashboardRoute(location.pathname));
  const { refreshCount } = useNotification();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const hasFetchedUnread = useRef(false);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const hideChatbotRoutes = ["/account/ai-chat"];
  const shouldHideChatbot = hideChatbotRoutes.includes(location.pathname);
  const isAiChatRoute = location.pathname === "/account/ai-chat";
  const hasFetchedDashboards = useRef(false);
  const [collapsed, setCollapsed] = useState(true);
  const { setSelectedMenuItem } = useMenu();
  const [hasApiError, setHasApiError] = useState(false);
  const [, setIsMobile] = useState(window.innerWidth <= 480);
  const api_key = localStorage.getItem("api_key");
  const user_id = localStorage.getItem("user_id");
  const [, setMode] = useState(
    window.innerWidth <= 768 ? "vertical" : "inline"
  );
  const [, setTotalUnreadCount] = useState(() => {
    const stored = localStorage.getItem("total_unread_count");
    return stored ? parseInt(stored, 10) : 0;
  });
  useEffect(() => {
    if (hasFetchedUnread.current) {return;}
    hasFetchedUnread.current = true;
    const fetchUnread = async () => {
      await refreshCount();
    };

    fetchUnread();
  }, [refreshCount]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (token) {
    try {
      const decodedToken = jwtDecode<DecodedToken>(token); // ✅ typed decode

      localStorage.setItem("user_id", decodedToken.user_id);
      localStorage.setItem("tenant_id", decodedToken.tenant_id);
      localStorage.setItem("role_id", decodedToken.role_id);
      localStorage.setItem("api_key", decodedToken.api_key);

      // Clean up the URL
      const newUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, document.title, newUrl);
    } catch (err) {
      console.error("Invalid token", err);
    }
  }

  useEffect(() => {
    const handleResize = () => {
      const newMode = window.innerWidth <= 768 ? "vertical" : "inline";
      setMode(newMode);

      if (!collapsed) {
        setCollapsed(true);
      }

      setIsMobile(window.innerWidth <= 480); // ✅ handles your mobile logic too
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [collapsed]);

  useEffect(() => {
    const storedUnreadCount = localStorage.getItem("total_unread_count");
    if (storedUnreadCount) {
      setTotalUnreadCount(parseInt(storedUnreadCount, 10)); // Use radix 10 for safe parsing
    }
  }, []);

  // Combined effect to handle all cases
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const sidebarEl = sidebarRef.current;
      if (!sidebarEl) {return;}

      const target = event.target as HTMLElement; // ✅ cast target

      const isSidebarClick = sidebarEl.contains(target);
      const isMenuClick = target.closest(
        ".ant-menu-item, .ant-menu-submenu-title"
      );
      const isSubMenuTrigger = target.closest(".ant-menu-submenu-arrow");

      if (!isSidebarClick && !collapsed) {
        // Case 1: Clicked outside - collapse if expanded
        setCollapsed(true);
      } else if (isSidebarClick) {
        if (collapsed) {
          // Case 2: Click anywhere on collapsed sidebar - expand
          setCollapsed(false);
        } else if (!isMenuClick && !isSubMenuTrigger && target === sidebarEl) {
          // Only collapse if the exact click target is the sidebar itself
          setCollapsed(true);
        } else if (!isMenuClick) {
          // Case 4: Click empty space inside expanded sidebar - collapse
          setCollapsed(true);
        }
        // Submenu clicks are handled by AntD automatically
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [collapsed]);

  document.body.style.overflowX = "hidden";

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  useEffect(() => {
    if (hasFetchedDashboards.current) {return;}
    hasFetchedDashboards.current = true;

    fetchDashboards();
  }, [user_id, api_key]);

  const fetchDashboards = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const api_key = localStorage.getItem("api_key");

      if (!user_id || !api_key) {
        message.error("Missing credentials. Please login again.");
        return;
      }

      await fetchAllDashboardsAPI(user_id, api_key, getApiLangFromI18n(i18n));
      setHasApiError(false);
    } catch (err) {
      const errorMessage =
        err instanceof DashboardApiError
          ? err.message
          : "Failed to fetch dashboards. Please try again.";
      message.error(errorMessage);
      if (isDashboardRoute) {
        setHasApiError(true);
      }
    }
  };

  const handleApiRetry = () => {
    setHasApiError(false);
    fetchDashboards();
  };

  const handleGlobalRetry = () => {
    apiErrorContext?.resetApiError();
    window.location.reload();
  };

  return (
    <Layout
      className="home-layout-shell"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "row" }}
    >
      <Sidebar />

      <Layout
        className={`home-layout-main ${collapsed ? "responsive-margin" : "header-style"} ${isAiChatRoute ? "ai-chat-layout-main" : ""}`}
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100vh",
        }}
      >
        {/************************************************* START OF HEADER SECTION ******************************************/}
        <Header
          className="header-dashboard d-flex justify-content-between"
          style={{
            background: colorBgContainer,
          }}
        >
          <div className="headline">
            {location.pathname ===
              SALES_DASHBOARD_ROUTE && (
              <>
                <img
                  src="/Icons/dashboard.svg"
                  alt="dashboard"
                  className="dashboard-image"
                ></img>
                <p className="dashboard-prefix">{t("sidebar.sections.dashboards")} /</p>

                <img
                  src="/Icons/sales.svg"
                  alt="dashboard"
                  className="chart-image"
                  style={{ height: "20px" }}
                ></img>
                <p className="dashboards-heading">
                  {" "}
                  {t("homepageHeader.dashboardTitles.salesAnalytics")}
                </p>
              </>
            )}
            {location.pathname ===
              `${PAID_MEDIA_DASHBOARD_ROUTE}/meta-ads-report` && (
              <>
                <img
                  src="/Icons/dashboard.svg"
                  alt="dashboard"
                  className="dashboard-image"
                ></img>
                <div className="dashboard-prefix">
                  {t("sidebar.sections.dashboards")} &nbsp;&nbsp; /{" "}
                </div>
                <img
                  src="/Icons/meta.svg"
                  alt="dashboard"
                  className="chart-image"
                  style={{ height: "20px" }}
                ></img>
                <p className="dashboards-heading">
                  {" "}
                  {t("homepageHeader.dashboardTitles.metaAdsOverview")}
                </p>
              </>
            )}
            {location.pathname ===
              WEB_DASHBOARD_ROUTE && (
              <>
                <img
                  src="/Icons/dashboard.svg"
                  alt="dashboard"
                  className="dashboard-image"
                ></img>
                <div className="dashboard-prefix">
                  {t("sidebar.sections.dashboards")} &nbsp;&nbsp; /{" "}
                </div>
                <ChartNetwork
                  className="chart-image"
                  style={{ height: "20px", color: "#2C68AD", stroke: "#2C68AD" }}
                />
                <p className="dashboards-heading">
                  {" "}
                  {t("homepageHeader.dashboardTitles.webAnalytics")}
                </p>
              </>
            )}
            {location.pathname === PAID_MEDIA_DASHBOARD_ROUTE && (
              <>
                <img
                  src="/Icons/dashboard.svg"
                  alt="dashboard"
                  className="dashboard-image"
                ></img>
                <div className="dashboard-prefix">
                  {t("sidebar.sections.dashboards")} &nbsp;&nbsp; /{" "}
                </div>
                <img
                  src="/Icons/coins.svg"
                  alt="dashboard"
                  className="chart-image"
                  style={{ height: "20px" }}
                ></img>
                <p className="dashboards-heading">
                  {" "}
                  {t("homepageHeader.dashboardTitles.paidMedia")}
                </p>
              </>
            )}
            {location.pathname === CUSTOMER_ATTRIBUTION_ROUTE && (
              <>
                <img
                  src="/Icons/dashboard.svg"
                  alt="dashboard"
                  className="dashboard-image"
                ></img>
                <div className="dashboard-prefix">
                  {t("sidebar.sections.dashboards")} &nbsp;&nbsp; /{" "}
                </div>
                <ChartArea className="chart-image" />
                <p className="dashboards-heading">
                  {" "}
                  {t("homepageHeader.dashboardTitles.customerAttribution")}
                </p>
              </>
            )}
            {location.pathname === SALES_PRODUCT_DASHBOARD_ROUTE && (
              <>
                <img
                  src="/Icons/dashboard.svg"
                  alt="dashboard"
                  className="dashboard-image"
                ></img>
                <div className="dashboard-prefix">
                  {t("sidebar.sections.dashboards")} &nbsp;&nbsp; /{" "}
                </div>
                <SquareChartGantt
                  className="chart-image"
                  style={{ height: "20px", color: "#2C68AD", stroke: "#2C68AD" }}
                />
                <p className="dashboards-heading">
                  {" "}
                  {t("homepageHeader.dashboardTitles.salesProductAnalytics")}
                </p>
              </>
            )}
            {location.pathname === OMNICHANNEL_DASHBOARD_ROUTE && (
              <>
                <img
                  src="/Icons/dashboard.svg"
                  alt="dashboard"
                  className="dashboard-image"
                ></img>
                <div className="dashboard-prefix">
                  {t("sidebar.sections.dashboards")} &nbsp;&nbsp; /{" "}
                </div>
                <SquareChartGantt
                  className="chart-image"
                  style={{ height: "20px", color: "#2C68AD", stroke: "#2C68AD" }}
                />
                <p className="dashboards-heading">
                  {" "}
                  {t("homepageHeader.dashboardTitles.omnichannelAnalytics")}
                </p>
              </>
            )}
            {location.pathname ===
              ORGANIC_DASHBOARD_ROUTE ? (
              <>
                <img
                  src="/Icons/dashboard.svg"
                  alt="dashboard"
                  className="dashboard-image"
                ></img>
                <div className="dashboard-prefix">
                  {t("sidebar.sections.dashboards")} &nbsp;&nbsp; /{" "}
                </div>
                <img src="/Icons/organicmaps.svg" className="chart-image" />
                <p className="dashboards-heading">
                  {" "}
                  {t("homepageHeader.dashboardTitles.organicMedia")}
                </p>
              </>
            ) : null}
            {location.pathname ===
              CLTV_DASHBOARD_ROUTE && (
              <>
                <img
                  src="/Icons/dashboard.svg"
                  alt="dashboard"
                  className="dashboard-image"
                ></img>
                <div className="dashboard-prefix">
                  {t("sidebar.sections.dashboards")} &nbsp;&nbsp; /
                </div>
                <img
                  src="/Icons/chart-histogram.svg"
                  alt="dashboard"
                  className="chart-image"
                  style={{ height: "20px" }}
                ></img>
                <p className="dashboards-heading">
                  {" "}
                  {t("homepageHeader.dashboardTitles.cltvAnalysis")}
                </p>
              </>
            )}
            {location.pathname === "/account/user-management" && (
              <>
                <img
                  src="/Icons/user.svg"
                  alt="user"
                  className="dashboard-image"
                ></img>
                {t("sidebar.sections.userManagement")}
              </>
            )}
            {location.pathname === "/account/help-and-support" && (
              <>
                <CircleHelp
                  className="dashboard-image"
                  style={{ height: "20px", width: "20px", color: "#2C68AD", stroke: "#2C68AD" }}
                />
                {t("sidebar.sections.helpAndSupport")}
              </>
            )}
            {location.pathname === "/account/social-listing-agent" && (
              <>
                <img
                  src="/Icons/add-user.svg"
                  alt="user"
                  className="social-icon"
                ></img>
                {t("sidebar.sections.socialListeningAgent")}
              </>
            )}
            {location.pathname === "/account/ai-chat" && (
              <>
                <img
                  src="/Icons/chatbot-logo.png"
                  alt="user"
                  className="dashboard-image-chatbot"
                ></img>
                {t("sidebar.sections.askDataTram")}
              </>
            )}
            {location.pathname === "/account/connect-sources" && (
              <>
                <img
                  src="/Icons/dynamic-source.svg"
                  alt="user"
                  className="dashboard-image"
                ></img>
                {t("sidebar.sections.connectedDataSources")}
              </>
            )}

            {location.pathname === "/account/my-account/account-settings" && (
              <>
                <img
                  src="/Icons/account.svg"
                  alt="dashboard"
                  style={{ marginInlineEnd: "7px" }}
                  className="dashboard-image"
                ></img>
                <p className="dashboard-prefix">
                  {" "}
                  {t("sidebar.sections.accountSettings")}&nbsp;&nbsp; /
                </p>
                <img
                  src="/Icons/account-settings.svg"
                  alt="dashboard"
                  className="chart-image"
                  style={{ height: "20px" }}
                ></img>{" "}
                <p className="dashboards-headings"> {t("sidebar.items.myAccount")}</p>
              </>
            )}

            {location.pathname ===
              "/account/my-account/notification-preferences" && (
              <>
                <img
                  className="dashboard-image"
                  src="/Icons/account.svg"
                  alt="dashboard"
                />
                <p className="dashboard-prefix">
                  {" "}
                  {t("sidebar.sections.accountSettings")}&nbsp;&nbsp; /
                </p>
                <img
                  src="/Icons/options.svg"
                  alt="dashboard"
                  className="top-sub-icons chart-image"
                />
                <p className="dashboards-headings">
                  {t("sidebar.items.notificationPreferences")}
                </p>
              </>
            )}

            {location.pathname === "/account/my-account/all-notifications" && (
              <>
                <img
                  className="dashboard-image"
                  src="/Icons/account.svg"
                  alt="dashboard"
                />
                <p className="dashboard-prefix">
                  {" "}
                  {t("sidebar.sections.accountSettings")}&nbsp;&nbsp; /
                </p>
                <img
                  src="/Icons/bell.svg"
                  alt="dashboard"
                  className="top-sub-icons chart-image "
                  style={{ marginInlineEnd: "7px" }}
                />
                <p className="dashboards-headings">
                  {t("sidebar.items.allNotifications")}
                </p>
              </>
            )}
            {location.pathname ===
              "/account/social-listing-agent/brand-analysis" && (
              <>
                <img
                  src="/Icons/add-user.svg"
                  alt="user"
                  className="social-icon"
                ></img>
                <p className="dashboard-prefix">
                  {" "}
                  {t("sidebar.sections.socialListeningAgent")}&nbsp;&nbsp;/
                </p>
                <img
                  src="/Icons/graph.svg"
                  alt="analysis"
                  className="social-icon"
                />
                <p className="dashboards-heading">
                  {" "}
                  {t("socialListingAgent.historyPage.analysisTypeBrand")}{" "}
                </p>
              </>
            )}

            {location.pathname ===
              "/account/social-listing-agent/competitive-analysis" && (
              <>
                <img
                  src="/Icons/add-user.svg"
                  alt="user"
                  className="social-icon"
                ></img>
                <p className="dashboard-prefix">
                  {" "}
                  {t("sidebar.sections.socialListeningAgent")}&nbsp;&nbsp;/
                </p>
                <img
                  src="/Icons/Globe-card.svg"
                  alt="competitive"
                  className="chart-image"
                  style={{ height: "20px" }}
                />
                <p className="dashboards-heading">
                  {" "}
                  {t("socialListingAgent.competitiveAnalysisTitle")}{" "}
                </p>
              </>
            )}
            {location.pathname ===
              "/account/social-listing-agent/competitive-report" && (
              <>
                <img
                  src="/Icons/add-user.svg"
                  alt="user"
                  className="social-icon"
                ></img>
                <p className="dashboard-prefix">
                  {" "}
                  {t("sidebar.sections.socialListeningAgent")}&nbsp;&nbsp;/
                </p>
                <img
                  src="/Icons/Clipboard.svg"
                  alt="competitive"
                  className="chart-image"
                  style={{ height: "20px" }}
                />
                <p className="dashboards-heading"> {t("socialListingAgent.report")} </p>
              </>
            )}
            {location.pathname.startsWith("/account/social-listing-agent/") &&
              !location.pathname.includes("brand-analysis") &&
              !location.pathname.includes("competitive-analysis") &&
              !location.pathname.includes("competitive-report") &&
              !location.pathname.includes("history") && (
                <>
                  <img
                    src="/Icons/add-user.svg"
                    alt="user"
                    className="social-icon"
                  ></img>
                  <p className="dashboard-prefix">
                    {" "}
                    {t("sidebar.sections.socialListeningAgent")}&nbsp;&nbsp;/
                  </p>
                  <img
                    src="/Icons/Clipboard.svg"
                    alt="competitive"
                    className="chart-image"
                    style={{ height: "20px" }}
                  />
                   <p className="dashboards-heading"> {t("socialListingAgent.report")} </p>
                </>
              )}
            {location.pathname === "/account/social-listing-agent/history" && (
              <>
                <img
                  src="/Icons/add-user.svg"
                  alt="user"
                  className="social-icon"
                ></img>
                <p className="dashboard-prefix">
                  {" "}
                  {t("sidebar.sections.socialListeningAgent")}&nbsp;&nbsp;/
                </p>
                <img
                  src="/Icons/Clipboard.svg"
                  alt="history"
                  className="chart-image"
                  style={{ height: "20px" }}
                />
                <p className="dashboards-heading">
                  {" "}
                  {t("socialListingAgent.historyPage.title")}{" "}
                </p>
              </>
            )}
          </div>

          {/************************************************* END OF HEADER SECTION ******************************************/}
          {/*
            `pe-3` is Bootstrap's padding-end utility, but this project loads the
            plain LTR-compiled `bootstrap.min.css` (see SourceConnector modals),
            not the `bootstrap.rtl.min.css` build — so `pe-3` compiles down to a
            literal `padding-right`, not a real logical property. Under RTL that
            padding stays on the physical right while the avatar ends up flush
            against the now-exposed physical left with no spacing at all. The
            inline `paddingInlineEnd` below is real CSS, so it actually flips.
          */}
          <div className="pe-3 d-flex align-items-center" style={{ paddingInlineEnd: "16px" }}>
            <div
              style={{
                flex: 1,
                maxWidth: "400px",
                paddingBottom: "20px",
                paddingInlineEnd: "30px",
              }}
            >
              <SmartSearch setSelectedMenuItem={setSelectedMenuItem} />
            </div>

            <div className="d-flex align-items-center" style={{ gap: "12px" }}>
              <div className="header-language-switcher">
                <LanguageSwitcher />
              </div>
              <NotificationBell setSelectedMenuItem={setSelectedMenuItem} />
              <DropDownProfile userType="user" />
            </div>
          </div>
        </Header>

        <div
          style={{
            padding: isAiChatRoute ? "4px" : "4px",
            flex: 1,
            minHeight: 0,
            display: "flex",
            overflow: "hidden",
            background: isAiChatRoute ? "#f3f4f6" : undefined,
          }}
        >
          <Content
            className={isAiChatRoute ? "ai-chat-layout-content" : undefined}
            style={{
              background: colorBgContainer,
              padding: isAiChatRoute ? 0 : 10,
              flex: 1,
              minHeight: 0,
              overflow: isAiChatRoute ? "hidden" : "auto",
            }}
          >
            {hasGlobalApiError ? (
              <ErrorFallback onRetry={handleGlobalRetry} />
            ) : hasApiError && isDashboardRoute ? (
              <ErrorFallback onRetry={handleApiRetry} />
            ) : (
              <Outlet />
            )}
          </Content>
        </div>
        {!shouldHideChatbot && <Chatbot />}
      </Layout>
      <AnnouncementPopupWrapper />
    </Layout>
  );
};

export default App;
